import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavLink {
  label: string;
  route: string;
}

const navLinks: NavLink[] = [
  { label: 'Inicio', route: '/' },
  { label: 'Sobre mí', route: '/sobre-mi' },
  { label: 'Servicios', route: '/servicios' },
  { label: 'Cómo lo hago', route: '/como-lo-hago' },
  { label: 'Casos de éxito', route: '/casos-de-exito' },
  { label: 'Blog', route: '/blog' },
  { label: 'Contacto', route: '/contacto' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const closeMobile = () => setMobileOpen(false);

  const isActive = (route: string) => {
    if (route === '/') return location.pathname === '/';
    return location.pathname.startsWith(route);
  };

  const mutedColor = 'text-background-50/80 hover:text-background-50';
  const activeColor = 'text-background-50 font-medium';
  const ctaStyle = 'border-background-50/70 text-background-50 hover:bg-background-50/10';

  const wordmark = (
    <img
      src="https://storage.helloreaddy.io/project_files/051d548c-4efa-4d12-87d7-897fd5389524/29249a17-99af-42bb-85f6-269475963b62_compressed_6b390e2f-f962-4d91-a1f1-dbfc689ca9f9.webp"
      alt="Erika López – Mentoría y Reflexión Estratégica"
      className="h-[70px] sm:h-[84px] md:h-[104px] w-auto object-contain brightness-0 invert"
    />
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-foreground-950/90 backdrop-blur-md border-b border-white/10'
            : 'bg-foreground-950/80'
        }`}
      >
        <nav className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-10 lg:px-16 h-[90px] md:h-[120px]" aria-label="Navegación principal">
          {/* Wordmark */}
          <Link
            to="/"
            onClick={closeMobile}
            className="flex-shrink-0 cursor-pointer relative z-[55]"
          >
            {wordmark}
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.route}
                to={link.route}
                className={`text-sm tracking-[0.03em] whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  isActive(link.route) ? activeColor : mutedColor
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* CTA */}
            <Link
              to="/agenda"
              className={`ml-1 sm:ml-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium tracking-[0.03em] whitespace-nowrap transition-all duration-300 cursor-pointer border ${ctaStyle}`}
            >
              Agenda una sesión
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer text-background-50 relative z-[55]"
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </nav>
      </header>

      {/* Mobile menu - outside header for clean z-index stacking */}
      <div
        className={`lg:hidden fixed inset-0 top-[84px] bg-foreground-950 z-40 transform transition-transform duration-400 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col items-center pt-24 pb-12 gap-8 overflow-y-auto">
          {navLinks.map((link) => (
            <Link
              key={link.route}
              to={link.route}
              onClick={closeMobile}
              className={`text-lg tracking-[0.03em] cursor-pointer transition-colors ${
                isActive(link.route) ? 'text-background-50 font-medium' : 'text-background-50/70 hover:text-background-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/agenda"
            onClick={closeMobile}
            className="mt-4 px-7 py-3 rounded-full text-sm font-medium tracking-[0.03em] bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Agenda una sesión
          </Link>
        </div>
      </div>
    </>
  );
}